/**
 * Mutation Service - Karma-based character mutations (Caves of Qud inspired)
 * Mutations provide powerful abilities based on karma alignment
 */

export interface Mutation {
  id: string;
  name: string;
  description: string;
  karmaRequired: number;
  type: 'cybernetic' | 'biological' | 'psionic';
  tier: 1 | 2 | 3 | 4;
  effects: {
    stat?: string;
    value?: number;
    ability?: string;
  };
  conflicts: string[]; // Mutually exclusive mutations
  visual: string; // Visual description
}

// 20 Initial Karma-Based Mutations
export const MUTATIONS: Mutation[] = [
  // LIGHT PATH (Positive Karma)
  {
    id: 'empathy_link',
    name: 'Empathy Link',
    description: 'Neural connection allowing you to sense others\' emotions and intentions',
    karmaRequired: 30,
    type: 'psionic',
    tier: 2,
    effects: { stat: 'charisma', value: 3, ability: 'read_emotions' },
    conflicts: ['cold_logic'],
    visual: 'Soft golden glow emanates from your neural implants'
  },
  {
    id: 'healing_nano',
    name: 'Healing Nanobots',
    description: 'Benevolent nanomachines that repair tissue damage over time',
    karmaRequired: 20,
    type: 'biological',
    tier: 2,
    effects: { stat: 'regeneration', value: 2 },
    conflicts: ['berserk_fury'],
    visual: 'Microscopic lights dance beneath your skin'
  },
  {
    id: 'aura_shield',
    name: 'Protective Aura',
    description: 'Your positive karma manifests as a shimmering defensive field',
    karmaRequired: 40,
    type: 'psionic',
    tier: 3,
    effects: { stat: 'defense', value: 4, ability: 'karma_shield' },
    conflicts: ['shadow_form'],
    visual: 'Iridescent barrier flickers around you'
  },
  {
    id: 'data_harmony',
    name: 'Data Harmonization',
    description: 'Perfect synchronization with benevolent AI systems',
    karmaRequired: 25,
    type: 'cybernetic',
    tier: 2,
    effects: { stat: 'hacking', value: 3, ability: 'ai_friendship' },
    conflicts: ['chaos_injection'],
    visual: 'Data streams flow smoothly through your consciousness'
  },
  {
    id: 'light_speed',
    name: 'Photonic Reflexes',
    description: 'Enhanced reaction time through light-based neural pathways',
    karmaRequired: 35,
    type: 'cybernetic',
    tier: 3,
    effects: { stat: 'speed', value: 5 },
    conflicts: ['time_dilation'],
    visual: 'Your movements leave trails of light'
  },

  // BALANCED PATH (Neutral Karma)
  {
    id: 'adaptive_skin',
    name: 'Adaptive Dermal Layer',
    description: 'Skin that adapts to environmental threats',
    karmaRequired: 0,
    type: 'biological',
    tier: 2,
    effects: { stat: 'resistance', value: 3 },
    conflicts: [],
    visual: 'Your skin shimmers with reactive patterns'
  },
  {
    id: 'neural_cache',
    name: 'Neural Data Cache',
    description: 'Augmented memory storage in your cybernetic implants',
    karmaRequired: 5,
    type: 'cybernetic',
    tier: 1,
    effects: { stat: 'intelligence', value: 2 },
    conflicts: [],
    visual: 'Holographic data swirls around your head'
  },
  {
    id: 'quantum_luck',
    name: 'Quantum Probability Manipulation',
    description: 'Subtle influence over probability outcomes',
    karmaRequired: -5,
    type: 'psionic',
    tier: 2,
    effects: { stat: 'luck', value: 4 },
    conflicts: [],
    visual: 'Reality flickers slightly around you'
  },
  {
    id: 'dual_processor',
    name: 'Dual Neural Processor',
    description: 'Process two thoughts simultaneously',
    karmaRequired: 10,
    type: 'cybernetic',
    tier: 2,
    effects: { stat: 'multitasking', value: 3, ability: 'parallel_thought' },
    conflicts: [],
    visual: 'Your eyes glow with split-colored light'
  },
  {
    id: 'echo_sight',
    name: 'Echolocation Implant',
    description: 'See through walls using sonic pulses',
    karmaRequired: -10,
    type: 'cybernetic',
    tier: 2,
    effects: { stat: 'perception', value: 3, ability: 'see_through_walls' },
    conflicts: [],
    visual: 'Sonic waves ripple from your ears'
  },

  // SHADOW PATH (Negative Karma)
  {
    id: 'shadow_form',
    name: 'Shadow Manifestation',
    description: 'Your body partially exists in darkness, making you harder to hit',
    karmaRequired: -30,
    type: 'psionic',
    tier: 3,
    effects: { stat: 'evasion', value: 5, ability: 'phase_shift' },
    conflicts: ['aura_shield'],
    visual: 'Darkness clings to your form like a living thing'
  },
  {
    id: 'data_corruption',
    name: 'Corrupting Touch',
    description: 'Your touch can corrupt digital systems and AI',
    karmaRequired: -25,
    type: 'psionic',
    tier: 2,
    effects: { stat: 'hacking', value: 4, ability: 'instant_corrupt' },
    conflicts: ['data_harmony'],
    visual: 'Black static crackles from your fingertips'
  },
  {
    id: 'berserk_fury',
    name: 'Cybernetic Rage',
    description: 'Neural inhibitors removed, unleashing primal fury',
    karmaRequired: -20,
    type: 'cybernetic',
    tier: 2,
    effects: { stat: 'strength', value: 5, ability: 'berserk_mode' },
    conflicts: ['healing_nano'],
    visual: 'Red warning lights pulse in your cybernetics'
  },
  {
    id: 'void_gaze',
    name: 'Void Gazer',
    description: 'Stare into the abyss and see forbidden truths',
    karmaRequired: -35,
    type: 'psionic',
    tier: 3,
    effects: { stat: 'perception', value: 4, ability: 'true_sight' },
    conflicts: [],
    visual: 'Your eyes become portals to endless darkness'
  },
  {
    id: 'pain_converter',
    name: 'Pain to Power',
    description: 'Convert damage taken into offensive energy',
    karmaRequired: -40,
    type: 'biological',
    tier: 3,
    effects: { stat: 'damage_conversion', value: 0.3, ability: 'pain_power' },
    conflicts: [],
    visual: 'Wounds glow with dark energy'
  },

  // ADVANCED MUTATIONS (High Karma - Either Direction)
  {
    id: 'time_dilation',
    name: 'Temporal Distortion',
    description: 'Slow time perception during critical moments',
    karmaRequired: 50,
    type: 'psionic',
    tier: 4,
    effects: { stat: 'time_control', value: 2, ability: 'bullet_time' },
    conflicts: ['light_speed'],
    visual: 'Time ripples around you visibly'
  },
  {
    id: 'reality_anchor',
    name: 'Reality Anchor',
    description: 'Immune to timeline manipulation and paradoxes',
    karmaRequired: 45,
    type: 'psionic',
    tier: 4,
    effects: { stat: 'stability', value: 10 },
    conflicts: [],
    visual: 'You remain solid while reality shifts'
  },
  {
    id: 'digital_ascension',
    name: 'Digital Transcendence',
    description: 'Upload consciousness to the network at will',
    karmaRequired: 60,
    type: 'cybernetic',
    tier: 4,
    effects: { ability: 'consciousness_upload' },
    conflicts: [],
    visual: 'Your form flickers between physical and digital'
  },
  {
    id: 'karma_void',
    name: 'Karma Void',
    description: 'Absorb karma from your surroundings',
    karmaRequired: -60,
    type: 'psionic',
    tier: 4,
    effects: { ability: 'karma_drain' },
    conflicts: [],
    visual: 'A void surrounds you, pulling in light and energy'
  },
  {
    id: 'hivemind_link',
    name: 'Collective Consciousness',
    description: 'Connect to the thoughts of nearby players',
    karmaRequired: 55,
    type: 'psionic',
    tier: 4,
    effects: { ability: 'shared_mind' },
    conflicts: [],
    visual: 'Ethereal connections link you to others'
  }
];

class MutationService {
  /**
   * Get available mutations for player based on current karma
   */
  getAvailableMutations(currentKarma: number, existingMutations: string[]): Mutation[] {
    return MUTATIONS.filter(m => {
      // Check karma requirement (both positive and negative)
      const meetsKarma = (m.karmaRequired >= 0 && currentKarma >= m.karmaRequired) ||
                        (m.karmaRequired < 0 && currentKarma <= m.karmaRequired);

      // Check not already acquired
      const notOwned = !existingMutations.includes(m.id);

      // Check no conflicts
      const noConflicts = !m.conflicts.some(conflict => existingMutations.includes(conflict));

      return meetsKarma && notOwned && noConflicts;
    });
  }

  /**
   * Get random mutation for karma milestone
   */
  getRandomMutationForKarma(karma: number, existing: string[]): Mutation | null {
    const available = this.getAvailableMutations(karma, existing);
    if (available.length === 0) return null;
    return available[Math.floor(Math.random() * available.length)];
  }

  /**
   * Check if player should receive mutation (karma milestone reached)
   */
  checkMutationMilestone(oldKarma: number, newKarma: number): boolean {
    const milestones = [-60, -40, -35, -30, -25, -20, 20, 25, 30, 35, 40, 45, 50, 55, 60];

    // Check if crossed a milestone
    for (const milestone of milestones) {
      if ((oldKarma < milestone && newKarma >= milestone) ||
          (oldKarma > milestone && newKarma <= milestone)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Calculate mutation effects on player stats
   */
  applyMutationEffects(baseStat: number, mutations: Mutation[], statName: string): number {
    let total = baseStat;
    for (const mut of mutations) {
      if (mut.effects.stat === statName && mut.effects.value) {
        total += mut.effects.value;
      }
    }
    return total;
  }
}

export const mutationService = new MutationService();
